import type { PersistedDestinationDraft } from '@/entities/destination'
import type {
  DestinationCenterPoint,
  DestinationFormValue,
  DestinationTranslationValue,
  IAreaBoundary,
  IDestination,
} from '@/shared/api/types/destination-types'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { buildDestinationDraft, hasDestinationDraftContent } from '@/entities/destination'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'
import {
  buildDestinationFormSnapshot,
  buildDestinationTranslationSnapshot,
  deriveFormFromDestination,
  deriveFormFromDraft,
  deriveTranslationsFromDraft,
  mapLocalizedDataToTranslations,
} from '@/features/destination/destination-form-model'
import {
  DEFAULT_DESTINATION_FORM,
  DESTINATION_DRAFT_ENTITY_ID,
} from '@/shared/constants/destination-form'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'

interface UseDestinationFormStateParams {
  isEdit: boolean
  /** Tenant admins may only view an existing destination, never change it. */
  isReadonly: boolean
  destinationId: string
  /** The destination being edited; `undefined` while loading or in create mode. */
  destination: IDestination | undefined
  supportedLanguages: string[]
}

/** Snapshots taken when the form loaded or was last saved; "dirty" means "differs from this". */
interface DestinationBaselines {
  form: string
  translations: string
}

function buildEmptyBaselines(): DestinationBaselines {
  return {
    form: buildDestinationFormSnapshot(DEFAULT_DESTINATION_FORM),
    translations: buildDestinationTranslationSnapshot([]),
  }
}

/**
 * Owns the destination form values and everything derived from them: hydration (edit data or restored draft),
 * dirty flags and the baselines the section "Save" buttons commit.
 */
export function useDestinationFormState({
  isEdit,
  isReadonly,
  destinationId,
  destination,
  supportedLanguages,
}: UseDestinationFormStateParams) {
  const [form, setForm] = useState<DestinationFormValue>(DEFAULT_DESTINATION_FORM)
  const [translations, setTranslations] = useState<DestinationTranslationValue[]>([])
  const [baselines, setBaselines] = useState<DestinationBaselines>(buildEmptyBaselines)

  // * VI: Edit dùng id destination làm entity id cho job dịch; create dùng id nháp chung vì destination không thuộc tenant. | EN: Edit uses the destination id as the translation-job entity id; create uses a shared draft id because destinations do not belong to a tenant.
  const translationEntityId = isEdit && destinationId ? destinationId : DESTINATION_DRAFT_ENTITY_ID
  const { clearEntityJobs, runningJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.DESTINATION,
    entityId: translationEntityId,
    audioEnabled: false,
  })

  const draftInput = useMemo(() => ({ form, translations }), [form, translations])
  const destinationDraft = useMemo(() => buildDestinationDraft(draftInput), [draftInput])
  const hasDestinationDraft = useMemo(
    () => hasDestinationDraftContent(draftInput, DEFAULT_DESTINATION_FORM),
    [draftInput],
  )
  const hydrateDestinationDraft = useCallback((persistedDraft?: PersistedDestinationDraft) => {
    startTransition(() => {
      setForm(deriveFormFromDraft(persistedDraft?.form))
      setTranslations(deriveTranslationsFromDraft(persistedDraft?.translations))
      setBaselines(buildEmptyBaselines())
    })
  }, [])
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.DESTINATION,
    entityId: translationEntityId,
    audioEnabled: false,
    initSignature: 'create:destination:global',
    draft: destinationDraft,
    hasContent: hasDestinationDraft,
    onHydrate: hydrateDestinationDraft,
  })

  // Edit mode: load the destination into the form once the tenant languages are known (one row per language).
  useEffect(() => {
    if (!destination || !isEdit || supportedLanguages.length < 1) {
      return
    }
    const nextForm = deriveFormFromDestination(destination)
    const nextTranslations = mapLocalizedDataToTranslations(
      destination.localizedData,
      supportedLanguages,
    )
    startTransition(() => {
      setForm(nextForm)
      setTranslations(nextTranslations)
      setBaselines({
        form: buildDestinationFormSnapshot(nextForm),
        translations: buildDestinationTranslationSnapshot(nextTranslations),
      })
    })
  }, [destination, isEdit, supportedLanguages])

  const formSnapshot = useMemo(() => buildDestinationFormSnapshot(form), [form])
  const translationSnapshot = useMemo(
    () => buildDestinationTranslationSnapshot(translations),
    [translations],
  )
  const isDetailsDirty = isEdit && formSnapshot !== baselines.form
  const isTranslationDirty = isEdit && translationSnapshot !== baselines.translations
  const hasChangedFromBaseline =
    formSnapshot !== baselines.form || translationSnapshot !== baselines.translations
  // * VI: Edit: dirty khi phần chi tiết hoặc bản dịch khác baseline đã lưu. Create: dirty khi có gì khác mặc định. Chế độ chỉ xem không bao giờ dirty. | EN: Edit: dirty when details or translations differ from the saved baseline. Create: dirty when anything differs from the defaults. Read-only never counts as dirty.
  const hasUnsavedChanges =
    !isReadonly && (isEdit ? isDetailsDirty || isTranslationDirty : hasChangedFromBaseline)

  const setAreaBoundary = useCallback(
    (areaBoundary: IAreaBoundary | null) => setForm((prev) => ({ ...prev, areaBoundary })),
    [],
  )
  const setCenterPoint = useCallback(
    (centerPoint: DestinationCenterPoint | null) => setForm((prev) => ({ ...prev, centerPoint })),
    [],
  )

  /** Called after a section saved successfully, so it stops counting as dirty. */
  const commitBaseline = {
    translations: () => {
      clearEntityJobs(TRANSLATION_JOB_SCOPE.DESTINATION, translationEntityId)
      setBaselines((prev) => ({ ...prev, translations: translationSnapshot }))
    },
    details: () => setBaselines((prev) => ({ ...prev, form: formSnapshot })),
    map: (boundary: IAreaBoundary, center: DestinationCenterPoint) =>
      setBaselines((prev) => ({
        ...prev,
        form: buildDestinationFormSnapshot({
          ...form,
          areaBoundary: boundary,
          centerPoint: center,
        }),
      })),
  }

  return {
    form,
    setForm,
    translations,
    setTranslations,
    setAreaBoundary,
    setCenterPoint,
    translationEntityId,
    runningJobCount: runningJobs.length,
    clearCurrentJobs,
    hasUnsavedChanges,
    commitBaseline,
  }
}
